import { get, post, put, del } from "./api";
import {
  AdminUser,
  LifvsUser,
  UserId,
  Receipt,
  RegisterPurchase,
  LifvsUserEvent,
  LifvsAdministratorEvent,
  UserStatus,
  UserStatusCount
} from "../models/User";
import { PagedList, ProblemDetails } from "../models/Http";
import {
  DatabaseError,
  NotFound,
  Success,
  unexpectedError,
  UnexpectedError,
  unexpectedHttpStatus,
  UnexpectedHttpStatus,
  ValidationFailed
} from "../models/ApiResults";
import { DiscountId } from "../models/Discount";
import { NotificationMessage } from "../models/Notification";
import { err, ok, Result, Unit } from "../result";
import { UserRule, UserRuleTestResult } from "../models/UserRule";
import { AgeVerificationModel } from "../models/AgeVerificationModel";

export const self = (): Promise<AdminUser> =>
  get("/v1/users/self").then(r => {
    if (r.status === 401) {
      return Promise.reject({
        reason: "UNAUTHORIZED"
      });
    }

    if (r.status === 403) {
      const user: AdminUser = { type: "ANONYMOUS" };
      return user;
    }

    return r.json() as Promise<AdminUser>;
  });

/* Get custom token for firebase auth */
export const getToken = async (): Promise<Result<ProblemDetails, string>> => {
  const response = await get("/auth/token");

  if (response.ok) {
    const content = await response.json();
    const token =
      typeof content === "string"
        ? content
        : typeof content?.value === "string"
        ? content.value
        : content?.value?.value;
    if (typeof token !== "string" || !token) {
      console.error("Firebase custom token missing from /auth/token", content);
      return Promise.reject({ reason: "TOKEN_MISSING", content });
    }
    return ok(token);
  }

  const body = await response.text();
  console.error("Failed to get Firebase custom token", response.status, body);
  return Promise.reject({
    reason: "TOKEN_FAILED",
    status: response.status,
    body
  });
};

export const list = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<LifvsUser>>
> => {
  const response = await get("/v1/users/list");
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<LifvsUser>);
  }

  return err(content as ProblemDetails);
};

export const search = async (
  searchPhrase: string,
  page: number = 1,
  pageSize: number = 10
): Promise<Result<ProblemDetails, PagedList<LifvsUser>>> => {
  const response = await get("/v1/users/search", {
    searchPhrase,
    page,
    pageSize
  });
  const content = await response.json();

  if (response.ok) {
    return ok(content as PagedList<LifvsUser>);
  }

  return err(content as ProblemDetails);
  /*.then(r => {
    if (r.status === 401) {
      return Promise.reject({
        reason: "UNAUTHORIZED"
      });
    }
    return r.json() as Promise<PagedList<LifvsUser>>;
  });*/
};

export type GetUserResult =
  | NotFound
  | Success<LifvsUser>
  | UnexpectedHttpStatus
  | UnexpectedError;
type PromiseResult<T> = T | PromiseLike<T>;

export const getById = (userId: UserId): Promise<GetUserResult> => {
  return get("/v1/users/" + userId)
    .then((response): PromiseResult<GetUserResult> => {
      switch (response.status) {
        case 404:
          return {
            type: "NOT_FOUND"
          };

        case 200:
          return response.json().then(data => ({
            type: "SUCCESS",
            data
          }));

        default:
          return unexpectedHttpStatus(response);
      }
    })
    .catch(unexpectedError);
};

export type AddDiscountResult =
  | NotFound
  | Success<LifvsUser>
  | ValidationFailed
  | DatabaseError
  | UnexpectedHttpStatus
  | UnexpectedError;

export const addDiscount = (
  userId: UserId,
  discountId: string
): Promise<AddDiscountResult> => {
  return post(`/v1/users/${userId}/discounts`, { id: discountId }).then(
    (response): PromiseResult<AddDiscountResult> => {
      switch (response.status) {
        case 404:
          return {
            type: "NOT_FOUND"
          };

        case 400:
          return response.json().then(details => ({
            type: "VALIDATION_FAILED",
            reason: details.errors ? details.errors.id[0] : details.detail
          }));

        case 409:
          return response.json().then(details => ({
            type: "DATABASE_ERROR",
            reason: details.errors ? details.errors.id[0] : details.detail
          }));

        case 201:
          return response.json().then(data => ({
            type: "SUCCESS",
            data
          }));

        default:
          return unexpectedHttpStatus(response);
      }
    }
  );
};

export const notify = (userId: UserId, message: NotificationMessage) =>
  post(`/v1/users/${userId}/notify`, message).then(response => {
    return response.status == 202;
  });

export const refund = async (
  userId: UserId,
  receipt: Receipt,
  fullRefund: boolean
) => {
  return await put(
    `/v1/users/${userId}/refund?fullRefund=${fullRefund}`,
    receipt
  );
};

export const registerPurchase = async (purchase: RegisterPurchase) =>
  await post(`/v1/users/${purchase.customerId}/purchases`, purchase);

export const updateEmployeeStatus = async (
  userId: UserId,
  Employee: boolean
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/${userId}/employee`, { Employee });

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const updateStatus = async (
  userId: UserId,
  status: UserStatus
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/${userId}/update-status`, { status });

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const unlock = async (
  userId: UserId
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/${userId}/unlock`);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const lock = async (
  userId: UserId,
  lockoutReason: string,
  comment: string
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/${userId}/lock`, {
    lockoutReason,
    comment
  });

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const deleteUser = async (
  userId: UserId
): Promise<Result<ProblemDetails, LifvsUser>> => {
  const response = await del(`/v1/users/${userId}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as LifvsUser);
  }

  return err(content as ProblemDetails);
};

export const addComment = async (
  userId: UserId,
  comment: string
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/${userId}/add-comment`, comment);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const listEvents = async (
  userId: UserId
): Promise<Result<ProblemDetails, ReadonlyArray<LifvsUserEvent>>> => {
  const response = await get(`/v1/users/${userId}/events`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<LifvsUserEvent>);
  }

  return err(content as ProblemDetails);
};

export const listAdministratorEvents = async (
  userId: UserId
): Promise<Result<ProblemDetails, ReadonlyArray<LifvsAdministratorEvent>>> => {
  const response = await get(`/v1/users/${userId}/history`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<LifvsAdministratorEvent>);
  }

  return err(content as ProblemDetails);
};

export const listAdministratorEventsByAdmin = async (
  userId: UserId
): Promise<Result<ProblemDetails, ReadonlyArray<LifvsAdministratorEvent>>> => {
  const response = await get(`/v1/users/${userId}/admin-history`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<LifvsAdministratorEvent>);
  }

  return err(content as ProblemDetails);
};

export const upsertRules = async (
  rules: ReadonlyArray<UserRule>
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/users/rules`, rules);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const getRules = async (
  outCome: string
): Promise<Result<ProblemDetails, ReadonlyArray<UserRule>>> => {
  const response = await get(`/v1/users/rules/${outCome}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<UserRule>);
  }

  return err(content as ProblemDetails);
};

export const getTestResult = async (
  rules: ReadonlyArray<UserRule>
): Promise<Result<ProblemDetails, ReadonlyArray<UserRuleTestResult>>> => {
  const response = await post(`/v1/users/rules/test`, rules);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<UserRuleTestResult>);
  }

  return err(content as ProblemDetails);
};

export const getUserStatusCount = async (
  status: string
): Promise<Result<ProblemDetails, UserStatusCount>> => {
  const response = await get(`/v1/users/status-count/${status}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as UserStatusCount);
  }

  return err(content as ProblemDetails);
};

export const getUserPurchases = async (
  userId: UserId
): Promise<Result<ProblemDetails, LifvsUser>> => {
  const response = await get(`/v1/users/${userId}/purchases`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as LifvsUser);
  }

  return err(content as ProblemDetails);
};

export const verifyAge = async (ageVerification: AgeVerificationModel) =>
  await post(`/v1/users/verify-age`, ageVerification);
